import { Launch } from '@carbon/icons-react';
import * as React from 'react';

import { useVoiceboxPanelData } from 'bones/components/molecules/Voicebox/context/VoiceboxPanelData';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { useVoiceboxSettings } from 'bones/components/molecules/Voicebox/context/VoiceboxSettings';
import { VoiceboxPanelLoadMore } from 'bones/components/molecules/Voicebox/panel/VoiceboxPanelLoadMore';
import { Attribute } from 'bones/constants/voicebox/panel';
import { VoiceboxSettings } from 'bones/constants/voicebox/settings';
import { Accordion } from 'bones/design-system/components/Accordion';
import { TableBody } from 'bones/design-system/components/data-table/TableBody';
import { Link } from 'bones/design-system/components/Link';
import { alphaSort } from 'bones/utils/sort/alphaSort';

export interface VoiceboxPanelAttributesProps {
  attributes: Attribute[];
  settings: VoiceboxSettings;
}

export const VoiceboxPanelAttributes: React.VFC<
  VoiceboxPanelAttributesProps
> = ({ attributes, settings }) => {
  const { copy, testIds } = useVoiceboxSchema();
  const { getLabelOrLocalName } = useVoiceboxPanelData();
  const { databasesById } = useVoiceboxSettings();

  if (!attributes.length) {
    return null;
  }

  const isMediaEnabled = databasesById[settings.databaseId]?.isMediaEnabled;

  return (
    <Accordion
      badgeProps={{ children: attributes.length.toLocaleString() }}
      className="vb-voicebox-panel-attributes"
      icon="left"
      initialIsOpen
      label={copy.panel.attributes.accordion}
      size="md"
      testId={testIds.panel.attributes.accordion}
    >
      <div className="vb-data-table">
        <TableBody
          body={[...attributes]
            .sort(
              alphaSort((attribute) =>
                getLabelOrLocalName(attribute.predicateIri)
              )
            )
            .map((attribute, attributeIdx) => (
              <React.Fragment key={attribute.predicateIri}>
                <td>{getLabelOrLocalName(attribute.predicateIri)}</td>
                <td>
                  <div>
                    {attribute.items.map((item, itemIdx) =>
                      isMediaEnabled && item.type === 'mediaLiteral' ? (
                        <Link
                          key={item.value}
                          href={item.value}
                          rel="noreferrer"
                          rightIcon={Launch}
                          size="md"
                          target="_blank"
                          testId={testIds.panel.attributes.link(
                            item.value,
                            itemIdx
                          )}
                        >
                          {item.value}
                        </Link>
                      ) : (
                        <React.Fragment key={item.value}>
                          {itemIdx ? <br /> : null}
                          {item.value}
                        </React.Fragment>
                      )
                    )}
                  </div>
                  <VoiceboxPanelLoadMore
                    data={attribute}
                    testId={testIds.panel.attributes.loadMore(
                      attribute.predicateIri,
                      attributeIdx
                    )}
                  />
                </td>
              </React.Fragment>
            ))}
          header={
            <>
              <td style={{ width: '50%' }}>{copy.panel.attributes.label}</td>
              <td style={{ width: '50%' }}>{copy.panel.attributes.value}</td>
            </>
          }
          size="lg"
        />
      </div>
    </Accordion>
  );
};
